import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthModule';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import ChatInlinePlayer from './ChatInlinePlayer';
import { buildKuiskaaCandidates, parseKuiskaaMessage } from './chatKuiskaaHelpers';
import { getEventAreaUnlockProductId } from './partials/profileHelpers';
import { useCreditProductUnlocked } from '../useStatisticsUnlock';
import EventAreaLockedNotice from './EventAreaLockedNotice';
import GameAnswerInlinePanel, { shouldCloseGameAnswerPanel } from './GameAnswerInlinePanel';

const API = process.env.REACT_APP_API_URL || '';

function parseJsonResponse(res) {
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    return res.text().then((text) => {
      throw new Error(text.startsWith('<!') ? 'Backend returned HTML. Is the API server running?' : text);
    });
  }
  return res.json();
}

function normalizeChatMessages(messages, currentUserId) {
  if (!Array.isArray(messages)) return [];
  const uid = currentUserId != null ? String(currentUserId) : null;
  return messages.map((m) => {
    if (uid != null && m.authorId != null) {
      return { ...m, isOwn: String(m.authorId) === uid };
    }
    return { ...m, isOwn: !!m.isOwn };
  });
}

function shortenReplyPreview(text, maxLen = 50) {
  if (typeof text !== 'string') return '';
  const t = text.trim();
  return t.length <= maxLen ? t : t.slice(0, maxLen) + '...';
}

function getEventStartTime(ev) {
  const [hh, mm = 0] = (ev?.time || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
  const d = new Date();
  if (ev?.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(ev.eventDate)) {
    const [y, m, day] = ev.eventDate.split('-').map((x) => parseInt(x, 10));
    d.setFullYear(y, m - 1, day);
  }
  d.setHours(hh, mm, 0, 0);
  return d;
}

function getEventEndTime(ev) {
  const start = getEventStartTime(ev);
  const durationMinutes = ev?.minutes != null ? Math.max(ev.minutes, 60) : 60;
  return new Date(start.getTime() + durationMinutes * 60 * 1000);
}

/** Finnish line for scheduled event end; null before start. */
function getEventEndsInHint(ev, nowMs) {
  const startMs = getEventStartTime(ev).getTime();
  if (nowMs < startMs) return null;
  const endMs = getEventEndTime(ev).getTime();
  const remMs = endMs - nowMs;
  if (remMs <= 0) return 'Tapahtuma on päättynyt';
  const remMin = Math.ceil(remMs / 60000);
  if (remMin < 60) {
    return `Tapahtuma loppuu ${remMin} minuutin kuluttua`;
  }
  const h = Math.max(1, Math.round(remMin / 60));
  return `Tapahtuma loppuu ${h} tunnin kuluttua`;
}

/** Create a game container message; success/score and notification-type messages get removeAt so they fade and are removed. */
function createGameMessage(text) {
  const id = `gm-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const isScoreSuccess =
    /Oikein! Sait pisteen!|Voitit! Pisteet:/.test(text) ||
    (String(text).includes('Pisteet:') && /Oikein!|Voitit!/.test(text));
  const isVäärin = /Väärin!?/.test(text);
  const isAlreadySolved = /Tämä on jo ratkaistu/.test(text);
  const isNotification = /Et ole tapahtuman järjestäjä|Vastaus olisi ollut|Vastaus vastaanotettu/.test(text);
  const isEventStatus = /Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(text);
  const removeAt = isEventStatus
    ? Date.now() + 3000
    : isNotification
      ? Date.now() + 1000
      : isVäärin
        ? Date.now() + 1000
        : (isScoreSuccess || isAlreadySolved)
          ? Date.now() + 2500
          : undefined;
  const shortFade = removeAt && (isNotification || isVäärin || isEventStatus);
  return { id, text, removeAt, shortFade: !!shortFade };
}

export default function EventChatPage({ eventId }) {
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [lastSentMessage, setLastSentMessage] = useState('');
  const [slashResponses, setSlashResponses] = useState([]);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const [gameDisplayPrompt, setGameDisplayPrompt] = useState(null);
  const [gameQuestionStartedAt, setGameQuestionStartedAt] = useState(null);
  const questionTimerRef = useRef(null);
  const [gameDisplayMessages, setGameDisplayMessages] = useState([]);
  const [mutedAuthorKeys, setMutedAuthorKeys] = useState(() => {
    try {
      const raw = localStorage.getItem('chatMutedAuthors');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const chatInputRef = useRef(null);
  const editInputRef = useRef(null);
  const [hasSentEventStartMessages, setHasSentEventStartMessages] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [gameStateEmoji, setGameStateEmoji] = useState(null);
  const emojiRoundEndedRef = useRef(null);
  const emojiRoundKeyClearedRef = useRef(null);
  const [gameDataReady, setGameDataReady] = useState(false);
  const [gameVastaaPanelOpen, setGameVastaaPanelOpen] = useState(false);
  const [gameVastaaSubmitting, setGameVastaaSubmitting] = useState(false);
  const areaUnlockProductId =
    event?.areaUnlockCreditProductId ?? getEventAreaUnlockProductId(event);
  const { loading: areaUnlockLoading, unlocked: areaUnlockPurchased } = useCreditProductUnlocked(
    user?.id,
    areaUnlockProductId,
  );

  useEffect(() => {
    if (!eventId) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [eventId]);

  useEffect(() => {
    if (!user) {
      window.location.href = '/';
      return;
    }
  }, [user]);

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    fetch(`${API}/api/events/${eventId}`)
      .then((res) => {
        if (!res.ok) {
          setEvent(null);
          setLoading(false);
          return;
        }
        return res.json();
      })
      .then((data) => {
        if (data !== undefined) setEvent(data);
        setLoading(false);
      })
      .catch(() => {
        setEvent(null);
        setLoading(false);
      });
  }, [eventId]);

  // When event has started, ensure welcome + first game question are posted to chat (once per event)
  useEffect(() => {
    if (!event || !eventId) return;
    if (hasSentEventStartMessages) return;
    const startsAt = getEventStartTime(event).getTime();
    if (!startsAt) return;
    const now = Date.now();
    if (now < startsAt) return;
    fetch(`${API}/api/events/${encodeURIComponent(event.id)}/start-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
      .catch(() => {})
      .finally(() => {
        setHasSentEventStartMessages(true);
      });
  }, [event, eventId, hasSentEventStartMessages]);

  const eventStarted = event && getEventStartTime(event).getTime() > 0 && now >= getEventStartTime(event).getTime();
  const eventEndsInHint = event ? getEventEndsInHint(event, now) : null;
  const eventHasEnded = !!(event && now >= getEventEndTime(event).getTime());
  const emojiGameEmojiRowClass =
    'EventLivestream-game-emoji-row' + (eventHasEnded ? ' EventLivestream-game-emoji-row--ended' : '');

  const isEmojiGame = event?.gameType === 'emoji-fi';
  const isRiddleOrTriviaGame = event?.gameType === 'riddle-fi' || event?.gameType === 'trivia-fi';

  // Sync gameStateEmoji from event when emoji game; mark game data ready when server has returned it (JSON read)
  useEffect(() => {
    if (event?.gameStateEmoji) {
      setGameStateEmoji(event.gameStateEmoji);
      setGameDataReady(true);
    } else if (!event?.hasGame || event?.gameType !== 'emoji-fi') {
      setGameStateEmoji(null);
    }
  }, [event?.gameStateEmoji, event?.hasGame, event?.gameType]);

  // Reset game data ready when event not started
  useEffect(() => {
    if (!eventStarted) setGameDataReady(false);
  }, [eventStarted]);

  // Emoji game: at start of each new round, clear "Vastaus vastaanotettu" notifications
  useEffect(() => {
    if (!isEmojiGame || !gameStateEmoji?.currentRound) return;
    const roundKey = gameStateEmoji.currentRound.roundStartedAt || gameStateEmoji.currentRound.questionId;
    if (!roundKey || emojiRoundKeyClearedRef.current === roundKey) return;
    emojiRoundKeyClearedRef.current = roundKey;
    setGameDisplayMessages((prev) => prev.filter((m) => !/Vastaus vastaanotettu/i.test(m.text)));
  }, [isEmojiGame, gameStateEmoji]);

  // Prune game messages that have passed their removeAt time (so they disappear over time and don't stack)
  useEffect(() => {
    const t = setInterval(() => {
      setGameDisplayMessages((prev) => {
        const now = Date.now();
        const next = prev.filter((m) => !m.removeAt || now < m.removeAt);
        return next.length === prev.length ? prev : next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // When event has started and has a game, load first question into game container on start; clear when no game or not started
  useEffect(() => {
    if (event?.eventType !== 'game' || !eventStarted) {
      setGameDisplayPrompt(null);
      setGameQuestionStartedAt(null);
      setGameDisplayMessages([]);
      return;
    }
    if (isEmojiGame) {
      const round = event?.gameStateEmoji?.currentRound;
      if (round) setGameDisplayPrompt(`${round.emojis || ''}\n${round.question || ''}`);
      setGameQuestionStartedAt(null);
      return;
    }
    if (!eventId) return;
    fetch(`${API}/api/events/${encodeURIComponent(eventId)}/next-question`)
      .then((res) => (res.ok ? res.json() : { prompt: null, noGame: true }))
      .then((data) => {
        setGameDataReady(true);
        if (data?.prompt) setGameDisplayPrompt(data.prompt);
        if (data?.questionStartedAt != null) setGameQuestionStartedAt(data.questionStartedAt);
        else if (!data?.prompt) setGameQuestionStartedAt(null);
      })
      .catch(() => {});
  }, [event?.id, event?.hasGame, event?.gameStateEmoji, event?.eventType, eventId, eventStarted, isEmojiGame]);

  // Emoji game: refetch event periodically to update current round / timer
  useEffect(() => {
    if (!eventId || !isEmojiGame || !eventStarted) return;
    const t = setInterval(() => {
      fetch(`${API}/api/events/${eventId}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((ev) => {
          if (ev?.gameStateEmoji) setGameStateEmoji(ev.gameStateEmoji);
        })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [eventId, isEmojiGame, eventStarted]);

  // Emoji game: end round only when 60s timer has run out (client checks; server enforces 60s)
  useEffect(() => {
    if (!eventId || !isEmojiGame || !gameStateEmoji?.currentRound) return;
    const round = gameStateEmoji.currentRound;
    const roundKey = round.roundStartedAt || round.questionId;
    if (!roundKey) return;
    if (emojiRoundEndedRef.current === roundKey) return;
    const startMs = round.roundStartedAt ? new Date(round.roundStartedAt).getTime() : 0;
    const ROUND_DURATION_MS = 60 * 1000;
    const elapsed = startMs > 0 ? Date.now() - startMs : 0;
    const timerRanOut = startMs > 0 && elapsed >= ROUND_DURATION_MS;
    if (timerRanOut) {
      emojiRoundEndedRef.current = roundKey;
      fetch(`${API}/api/events/${encodeURIComponent(eventId)}/game-round-end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user?.id }),
      })
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          if (data?.roundNotEndedYet && data?.gameStateEmoji) {
            setGameStateEmoji(data.gameStateEmoji);
            emojiRoundEndedRef.current = null;
            return;
          }
          if (data?.gameStateEmoji) {
            setGameStateEmoji(data.gameStateEmoji);
            if (data.gameStateEmoji.currentRound) {
              emojiRoundEndedRef.current = null;
              setGameDisplayPrompt(
                `${data.gameStateEmoji.currentRound.emojis || ''}\n${data.gameStateEmoji.currentRound.question || ''}`,
              );
              setGameDisplayMessages((prev) => prev.filter((m) => !/Vastaus vastaanotettu/i.test(m.text)));
            }
          }
          fetch(`${API}/api/events/${eventId}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((ev) => { if (ev) setEvent(ev); })
            .catch(() => {});
        })
        .catch(() => {});
    }
  }, [eventId, isEmojiGame, gameStateEmoji, now, user?.id]);

  // 60s per-question timer for riddle/trivia: when time runs out, call timeout-skip and show next question
  useEffect(() => {
    if (isEmojiGame || !eventId || !gameDisplayPrompt || gameQuestionStartedAt == null) {
      if (questionTimerRef.current) {
        clearTimeout(questionTimerRef.current);
        questionTimerRef.current = null;
      }
      return;
    }
    const delayMs = 60 * 1000 - (Date.now() - gameQuestionStartedAt);
    const runTimeout = () => {
      questionTimerRef.current = null;
      fetch(`${API}/api/events/${encodeURIComponent(eventId)}/game-skip?timeout=1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user?.id }),
      })
        .then((res) => (res.ok ? res.json() : {}))
        .then((data) => {
          if (data?.nextQuestionPrompt != null) setGameDisplayPrompt(data.nextQuestionPrompt);
          else setGameDisplayPrompt(null);
          if (data?.questionStartedAt != null) setGameQuestionStartedAt(data.questionStartedAt);
          else setGameQuestionStartedAt(null);
        })
        .catch(() => {});
    };
    if (delayMs <= 0) {
      runTimeout();
      return;
    }
    questionTimerRef.current = setTimeout(runTimeout, delayMs);
    return () => {
      if (questionTimerRef.current) {
        clearTimeout(questionTimerRef.current);
        questionTimerRef.current = null;
      }
    };
  }, [eventId, isEmojiGame, gameDisplayPrompt, gameQuestionStartedAt, user?.id]);

  useEffect(() => {
    setGameVastaaPanelOpen(false);
  }, [gameDisplayPrompt, gameStateEmoji?.currentRound?.roundStartedAt, gameStateEmoji?.currentRound?.questionId]);

  const submitGameAnswer = useCallback(
    (answer) => {
      const trimmed = (answer || '').trim();
      if (!trimmed) {
        setGameDisplayMessages((prev) => [
          ...prev,
          createGameMessage('Käytä komentoa näin: /vastaa tai /arvaa sinun vastauksesi'),
        ]);
        return Promise.resolve(null);
      }
      if (!user || !eventId) return Promise.resolve(null);
      if (event && now >= getEventEndTime(event).getTime()) {
        setGameDisplayMessages((prev) => [...prev, createGameMessage('Tapahtuma on päättynyt')]);
        return Promise.resolve(null);
      }
      return fetch(`${API}/api/events/${encodeURIComponent(eventId)}/game-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          displayName: user.displayName || user.username,
          answer: trimmed,
        }),
      })
        .then((res) => parseJsonResponse(res))
        .then((data) => {
          const text = data?.result || 'Vastausta ei voitu tarkistaa.';
          if (eventStarted && text === 'Peli ei ole vielä käynnissä.') {
            if (data?.gameStateEmoji) setGameStateEmoji(data.gameStateEmoji);
            if (data?.nextQuestionPrompt != null) {
              setGameDisplayPrompt(data.nextQuestionPrompt);
              if (data?.questionStartedAt != null) setGameQuestionStartedAt(data.questionStartedAt);
            } else if (event?.gameType !== 'emoji-fi') {
              setGameDisplayPrompt(null);
              setGameQuestionStartedAt(null);
            }
            if (eventId) {
              fetch(`${API}/api/events/${encodeURIComponent(eventId)}`)
                .then((res) => (res.ok ? res.json() : null))
                .then((ev) => {
                  if (ev) setEvent(ev);
                })
                .catch(() => {});
            }
            return data;
          }
          const msg = createGameMessage(text);
          const isEventStatusMsg = /Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(text);
          setGameDisplayMessages((prev) => {
            let filtered = prev;
            if (isEventStatusMsg) {
              filtered = filtered.filter(
                (m) => !/Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(m.text),
              );
            }
            if (msg.removeAt) {
              filtered = filtered.filter((m) => !/^Vastaus ei ole /i.test(m.text) && !/Väärin!?/.test(m.text));
            }
            return [...filtered, msg];
          });
          if (msg.removeAt) {
            const id = msg.id;
            const removeDelay = /Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(msg.text)
              ? 3000
              : /Väärin!?|Vastaus vastaanotettu/.test(msg.text)
                ? 1000
                : 2500;
            setTimeout(() => {
              setGameDisplayMessages((prev) => prev.filter((m) => m.id !== id));
            }, removeDelay);
          }
          if (data?.gameStateEmoji) setGameStateEmoji(data.gameStateEmoji);
          if (data?.nextQuestionPrompt != null) {
            setGameDisplayPrompt(data.nextQuestionPrompt);
            if (data?.questionStartedAt != null) setGameQuestionStartedAt(data.questionStartedAt);
          } else if (event?.gameType !== 'emoji-fi') {
            setGameDisplayPrompt(null);
            setGameQuestionStartedAt(null);
          }
          if (eventId) {
            fetch(`${API}/api/events/${encodeURIComponent(eventId)}`)
              .then((res) => (res.ok ? res.json() : null))
              .then((ev) => {
                if (ev) setEvent(ev);
              })
              .catch(() => {});
          }
          return data;
        })
        .catch(() => {
          setGameDisplayMessages((prev) => [...prev, createGameMessage('Vastausta ei voitu tarkistaa.')]);
          return null;
        });
    },
    [eventStarted, event, eventId, user, now],
  );

  const handleInlineVastaaSubmit = useCallback(
    (answer) => {
      setGameVastaaSubmitting(true);
      submitGameAnswer(answer)
        .then((data) => {
          if (shouldCloseGameAnswerPanel(data)) setGameVastaaPanelOpen(false);
        })
        .finally(() => setGameVastaaSubmitting(false));
    },
    [submitGameAnswer],
  );

  const fetchChat = useCallback(() => {
    if (!eventId || !user) return;
    const username = user?.displayName || user?.username || '';
    const url = username
      ? `${API}/api/chat/event/${eventId}?username=${encodeURIComponent(username)}`
      : `${API}/api/chat/event/${eventId}`;
    fetch(url)
      .then((res) => parseJsonResponse(res))
      .then((data) => setChatMessages(normalizeChatMessages(data.messages || [], user?.id)))
      .catch(() => setChatMessages([]));
  }, [eventId, user]);

  useEffect(() => {
    if (!user || !eventId) return;
    fetchChat();
    const t = setInterval(fetchChat, 2000);
    return () => clearInterval(t);
  }, [user, eventId, fetchChat]);

  const kuiskaaCandidates = useMemo(() => buildKuiskaaCandidates(), []);

  const reloadKysyQuestion = useCallback(() => {
    if (!eventId || !user) return;
    fetch(`${API}/api/events/${encodeURIComponent(eventId)}/next-question?resend=1`)
      .then((res) => (res.ok ? res.json() : { prompt: null, noGame: true }))
      .then((data) => {
        if (data?.prompt) {
          setGameDisplayPrompt(data.prompt);
          if (data?.questionStartedAt != null) setGameQuestionStartedAt(data.questionStartedAt);
        } else {
          setGameDisplayMessages((prev) => [...prev, createGameMessage('Tapahtumalla ei ole peliä.')]);
        }
      })
      .catch(() => {
        setGameDisplayMessages((prev) => [...prev, createGameMessage('Tapahtumalla ei ole peliä.')]);
      });
  }, [eventId, user]);

  const handleSendChat = useCallback((e) => {
    e.preventDefault();
    const raw = (chatInput || '').trim();
    if (!raw || !user || !eventId) return;
    // Handle resend / show question: /kysy — repeat current question in game container (resend=1 = do not pick new)
    if (/^\/kysy\s*$/i.test(raw)) {
      setChatInput('');
      setReplyingToMessage(null);
      reloadKysyQuestion();
      return;
    }
    // Handle host-only skip riddle: /ohi — reveal answer and go to next (owner only)
    if (/^\/ohi\s*$/i.test(raw)) {
      setChatInput('');
      setReplyingToMessage(null);
      fetch(`${API}/api/events/${encodeURIComponent(eventId)}/game-skip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      })
        .then((res) => res.json())
        .then((data) => {
          const text = data?.result || '—';
          if (eventStarted && text === 'Peli ei ole vielä käynnissä.') {
            if (data?.nextQuestionPrompt != null) {
              setGameDisplayPrompt(data.nextQuestionPrompt);
              if (data?.questionStartedAt != null) setGameQuestionStartedAt(data.questionStartedAt);
            } else {
              setGameDisplayPrompt(null);
              setGameQuestionStartedAt(null);
            }
            return;
          }
          const msg = createGameMessage(text);
          const isEventStatusMsg = /Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(text);
          setGameDisplayMessages((prev) => {
            let filtered = prev.filter((m) => !/Vastaus olisi ollut/.test(m.text));
            if (isEventStatusMsg) {
              filtered = filtered.filter((m) => !/Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(m.text));
            }
            return [...filtered, msg];
          });
          if (msg.removeAt) {
            const id = msg.id;
            const removeDelay = /Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(msg.text) ? 3000 : 1000;
            setTimeout(() => {
              setGameDisplayMessages((prev) => prev.filter((m) => m.id !== id));
            }, removeDelay);
          }
          if (data?.nextQuestionPrompt != null) {
            setGameDisplayPrompt(data.nextQuestionPrompt);
            if (data?.questionStartedAt != null) setGameQuestionStartedAt(data.questionStartedAt);
          } else {
            setGameDisplayPrompt(null);
            setGameQuestionStartedAt(null);
          }
        })
        .catch(() => {
          setGameDisplayMessages((prev) => [...prev, createGameMessage('Komento epäonnistui.')]);
        });
      return;
    }
    // Handle game answer: /vastaa or /arvaa — result and next question go to game container
    const match = raw.match(/^\/(?:vastaa|arvaa)(?:\s+(.*))?$/i);
    if (match) {
      const answer = (match[1] || '').trim();
      setChatInput('');
      setReplyingToMessage(null);
      submitGameAnswer(answer);
      return;
    }
    const kuiskaaParsed = parseKuiskaaMessage(raw);
    if (kuiskaaParsed) {
      setChatInput('');
      setReplyingToMessage(null);
      setLastSentMessage(raw);
      const text = kuiskaaParsed.body.slice(0, 500);
      const userMsg = {
        text,
        authorName: user.displayName || user.username,
        authorId: user.id,
        eventId,
        whisperTo: kuiskaaParsed.target.slice(0, 100),
      };
      const optimisticMessage = {
        ...userMsg,
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        isOwn: true,
        createdAt: new Date().toISOString(),
      };
      setChatMessages((prev) => [...prev, optimisticMessage]);
      fetch(`${API}/api/chat/event/${eventId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: [userMsg] }),
      })
        .then((res) => parseJsonResponse(res))
        .then((data) => {
          setChatMessages(normalizeChatMessages(data.messages || [], user.id));
          setScrollTrigger((t) => t + 1);
        })
        .catch(() => {});
      return;
    }
    setLastSentMessage(raw);
    const text = raw.slice(0, 500);
    const userMsg = {
      text,
      authorName: user.displayName || user.username,
      authorId: user.id,
      eventId,
      ...(replyingToMessage && {
        replyTo: {
          messageId: replyingToMessage.id,
          textPreview: shortenReplyPreview(replyingToMessage.text),
        },
      }),
    };
    // Optimistically show the user's message immediately
    const optimisticMessage = {
      ...userMsg,
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      isOwn: true,
      createdAt: new Date().toISOString(),
    };
    setChatMessages((prev) => [...prev, optimisticMessage]);
    setChatInput('');
    setReplyingToMessage(null);
    fetch(`${API}/api/chat/event/${eventId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: [userMsg] }),
    })
      .then((res) => parseJsonResponse(res))
      .then((data) => {
        setChatMessages(normalizeChatMessages(data.messages || [], user.id));
        setScrollTrigger((t) => t + 1);
      })
      .catch(() => {});
  }, [chatInput, replyingToMessage, user, eventId, eventStarted, submitGameAnswer, reloadKysyQuestion]);

  const handleDeleteOwnMessage = useCallback((id) => {
    if (!window.confirm('Haluatko varmasti poistaa tämän viestin?')) return;
    if (editingMessageId === id) {
      setEditingMessageId(null);
      setEditingDraft('');
    }
    const next = chatMessages.filter((m) => m.id !== id);
    setChatMessages(normalizeChatMessages(next, user?.id));
    fetch(`${API}/api/chat/event/${eventId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: next }),
    }).catch(() => {});
  }, [chatMessages, editingMessageId, user?.id, eventId]);

  const handleStartEditOwnMessage = useCallback((msg) => {
    setEditingMessageId(msg.id);
    setEditingDraft(msg.text);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingMessageId == null) return;
    const text = editingDraft.trim().slice(0, 500);
    const updatedAt = new Date().toISOString();
    const next = chatMessages.map((m) =>
      m.id === editingMessageId ? { ...m, text: text || m.text, updatedAt } : m
    );
    setChatMessages(normalizeChatMessages(next, user?.id));
    setEditingMessageId(null);
    setEditingDraft('');
    fetch(`${API}/api/chat/event/${eventId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: next }),
    }).catch(() => {});
  }, [editingMessageId, editingDraft, chatMessages, user?.id, eventId]);

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditingDraft('');
  }, []);

  const handleReplyToMessage = useCallback((msg) => {
    setReplyingToMessage({ id: msg.id, text: msg.text || '', authorName: msg.authorName });
    chatInputRef.current?.focus();
  }, []);

  const handleCancelReply = useCallback(() => setReplyingToMessage(null), []);

  const handleDismissSlashMessage = useCallback((id) => {
    setSlashResponses((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const handleEditInputKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSaveEdit();
    }
  }, [handleCancelEdit, handleSaveEdit]);

  const handleToggleMuteAuthor = useCallback((authorKey) => {
    if (!authorKey) return;
    setMutedAuthorKeys((prev) => {
      const next = prev.includes(authorKey) ? prev.filter((k) => k !== authorKey) : [...prev, authorKey];
      try {
        localStorage.setItem('chatMutedAuthors', JSON.stringify(next));
      } catch (e) {
        console.warn(e);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (editingMessageId != null && editInputRef.current) editInputRef.current.focus();
  }, [editingMessageId]);

  if (loading) {
    return (
      <div className="App-block">
        <p>Loading...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="App-block">
        <p>Event not found.</p>
        <button type="button" className="Button" onClick={() => window.location.href = '/'}>
          Back
        </button>
      </div>
    );
  }

  const isOwnEventChat =
    event.createdByUserId != null &&
    user?.id != null &&
    String(event.createdByUserId) === String(user.id);
  const needsAreaUnlockCheckChat = !!areaUnlockProductId && !isOwnEventChat;
  const showAreaLockedChat = needsAreaUnlockCheckChat && !areaUnlockLoading && !areaUnlockPurchased;
  const showAreaLoadingChat = needsAreaUnlockCheckChat && areaUnlockLoading;

  return (
    <div className="EventLivestream-page EventChatPage">
      <div className="EventLivestream-topbar">

        <div className="EventLivestream-topbar-info">

          <h2 className="EventLivestream-title">Event Chat: {event.title || 'Event'}</h2>
          <span className="EventLivestream-author">By {event.author || '—'}</span>
        </div>

      </div>
      {event.eventType === 'game' && eventStarted && gameDataReady && (
        <div className="EventLivestream-game-container EventChatPage-game-container">
          {showAreaLoadingChat ? (
            <p className="EventLivestream-area-locked EventLivestream-area-locked--loading">Tarkistetaan…</p>
          ) : showAreaLockedChat ? (
            <EventAreaLockedNotice />
          ) : (
            <>
              {isEmojiGame && (() => {
                const round = gameStateEmoji?.currentRound;
                const startMs = round?.roundStartedAt ? new Date(round.roundStartedAt).getTime() : 0;
                const secondsLeft = startMs ? Math.max(0, Math.ceil((startMs + 60000 - now) / 1000)) : 60;
                const emojiRowClickable = isEmojiGame && !eventHasEnded;
                return (
                  round && (
                    <>
                      <div
                        className={
                          emojiGameEmojiRowClass +
                          (emojiRowClickable ? ' EventLivestream-game-clickable-answer' : '')
                        }
                        role={emojiRowClickable ? 'button' : undefined}
                        tabIndex={emojiRowClickable ? 0 : undefined}
                        onClick={emojiRowClickable ? () => setGameVastaaPanelOpen(true) : undefined}
                        onKeyDown={
                          emojiRowClickable
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setGameVastaaPanelOpen(true);
                                }
                              }
                            : undefined
                        }
                      >
                        {round.emojis}
                      </div>
                      <div className="EventLivestream-game-emoji-question">{round.question}</div>
                      <div className="EventLivestream-game-emoji-timer">
                        Aikaa: {secondsLeft} s
                      </div>
                      <GameAnswerInlinePanel
                        open={gameVastaaPanelOpen}
                        onClose={() => setGameVastaaPanelOpen(false)}
                        onSubmitAnswer={handleInlineVastaaSubmit}
                        submitting={gameVastaaSubmitting}
                      />
                    </>
                  )
                );
              })()}
              {!isEmojiGame && gameDisplayPrompt && (
                isRiddleOrTriviaGame ? (
                  <>
                    <div
                      className={
                        'EventLivestream-game-prompt' +
                        (!eventHasEnded ? ' EventLivestream-game-clickable-answer' : '')
                      }
                      role={!eventHasEnded ? 'button' : undefined}
                      tabIndex={!eventHasEnded ? 0 : undefined}
                      onClick={!eventHasEnded ? () => setGameVastaaPanelOpen(true) : undefined}
                      onKeyDown={
                        !eventHasEnded
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setGameVastaaPanelOpen(true);
                              }
                            }
                          : undefined
                      }
                    >
                      {gameDisplayPrompt}
                    </div>
                    <GameAnswerInlinePanel
                      open={gameVastaaPanelOpen}
                      onClose={() => setGameVastaaPanelOpen(false)}
                      onSubmitAnswer={handleInlineVastaaSubmit}
                      submitting={gameVastaaSubmitting}
                    />
                  </>
                ) : (
                  <div className="EventLivestream-game-prompt">{gameDisplayPrompt}</div>
                )
              )}
              {isEmojiGame && gameDisplayPrompt && !gameStateEmoji?.currentRound && (() => {
                const raw = String(gameDisplayPrompt);
                const nl = raw.indexOf('\n');
                const emojiLine = (nl === -1 ? raw : raw.slice(0, nl)).trim();
                const questionRest = nl === -1 ? '' : raw.slice(nl + 1).trim();
                const emojiRowClickable = isEmojiGame && !eventHasEnded;
                return (
                  <>
                    {emojiLine ? (
                      <div
                        className={
                          emojiGameEmojiRowClass +
                          (emojiRowClickable ? ' EventLivestream-game-clickable-answer' : '')
                        }
                        role={emojiRowClickable ? 'button' : undefined}
                        tabIndex={emojiRowClickable ? 0 : undefined}
                        onClick={emojiRowClickable ? () => setGameVastaaPanelOpen(true) : undefined}
                        onKeyDown={
                          emojiRowClickable
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setGameVastaaPanelOpen(true);
                                }
                              }
                            : undefined
                        }
                      >
                        {emojiLine}
                      </div>
                    ) : null}
                    {questionRest ? (
                      <div className="EventLivestream-game-emoji-question">{questionRest}</div>
                    ) : null}
                    <GameAnswerInlinePanel
                      open={gameVastaaPanelOpen}
                      onClose={() => setGameVastaaPanelOpen(false)}
                      onSubmitAnswer={handleInlineVastaaSubmit}
                      submitting={gameVastaaSubmitting}
                    />
                  </>
                );
              })()}
              {!gameDisplayPrompt && !(isEmojiGame && gameStateEmoji?.currentRound) && (
                <div
                  className={
                    'EventLivestream-game-fetch-prompt' +
                    (user && eventId ? ' EventLivestream-game-fetch-prompt--clickable' : '')
                  }
                  role={user && eventId ? 'button' : undefined}
                  tabIndex={user && eventId ? 0 : undefined}
                  onClick={user && eventId ? () => reloadKysyQuestion() : undefined}
                  onKeyDown={
                    user && eventId
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            reloadKysyQuestion();
                          }
                        }
                      : undefined
                  }
                  aria-label={user && eventId ? 'Näytä kysymys' : undefined}
                  title={user && eventId ? 'Näytä kysymys' : undefined}
                >
                  <p>Kirjoita chatissa <code>/kysy</code> nähdäksesi kysymyksen.</p>
                </div>
              )}
              <div className="EventLivestream-game-messages">
                {gameDisplayMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`EventLivestream-game-message${msg.removeAt ? ' EventLivestream-game-message--fade' : ''}${msg.shortFade ? ' EventLivestream-game-message--fade-1s' : ''}`}
                  >
                    {msg.text}
                  </div>
                ))}
              </div>
              <div className="EventLivestream-game-commands" aria-label="Komennot">
                {eventEndsInHint ? (
                  <p className="EventLivestream-game-hint">{eventEndsInHint}</p>
                ) : null}
                <span className="EventLivestream-game-commands-title">Komennot:</span>
                <ul className="EventLivestream-game-commands-list">
                  <li><code>/vastaa vastaus</code> — lähetä vastaus</li>
                  <li><code>/vastaa</code> tai <code>/arvaa vastaus</code> — lähetä vastaus arvoitukseen</li>
                  <li><code>/kysy</code> — kysy uudelleen</li>
                  {isOwnEventChat && (
                    <li><code>/ohi</code> — vain järjestäjä: paljasta vastaus ja siirry seuraavaan</li>
                  )}
                </ul>
              </div>
            </>
          )}
        </div>
      )}
      <div className="EventChatPage-chat">
        <div className="EventLivestream-chat-messages EventChatPage-messages">
          <ChatMessages
            messages={[...chatMessages, ...slashResponses]}
            editingMessageId={editingMessageId}
            editingDraft={editingDraft}
            setEditingDraft={setEditingDraft}
            editInputRef={editInputRef}
            onStartEdit={handleStartEditOwnMessage}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onDeleteMessage={handleDeleteOwnMessage}
            onReply={handleReplyToMessage}
            onEditInputKeyDown={handleEditInputKeyDown}
            currentUserDisplayName={user?.displayName || user?.username}
            profileRedirectPath={`/event/stream/${eventId}`}
            mutedAuthorKeys={mutedAuthorKeys}
            onToggleMuteAuthor={handleToggleMuteAuthor}
            onDismissMessage={handleDismissSlashMessage}
            autoScroll={false}
            scrollToEndTrigger={scrollTrigger}
          />
        </div>
        <ChatInput
          inputRef={chatInputRef}
          value={chatInput}
          onChangeValue={setChatInput}
          onSubmit={handleSendChat}
          lastSentMessage={lastSentMessage}
          placeholder="Kirjoita viesti..."
          maxLength={500}
          leftSlot={<ChatInlinePlayer />}
          replyingTo={replyingToMessage}
          onCancelReply={handleCancelReply}
          mentionCandidates={Array.from(
            new Set([
              'pulinabotti',
              ...(chatMessages || []).map((m) => m.authorName).filter(Boolean),
            ])
          )}
          kuiskaaCandidates={kuiskaaCandidates}
          currentUser={user}
        />
      </div>
    </div>
  );
}
